import { ChangeEvent, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { RetailBuyerSelector } from 'src/components/Selectors/BuyerSelector';
import { CurrencyFilterSelector } from 'src/components/Selectors/CurrencyFilterSelector';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { SectionContent } from 'src/modals/common/Detail/Detail.styled';
import { DetailSection } from 'src/modals/common/DetailSection';
import { ContentId } from 'src/utils/constants/contentId';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { ListingDetails } from 'src/WebApiController';

import { RetailSaleFormValues } from '../types';
import {
  AddNewBuyerForm,
  SaleTags,
  TicketSection,
} from './RetailSaleBuyerStep.components';
import * as styles from './RetailSaleBuyerStep.css';

type RetailSaleBuyerStepProps = {
  listing?: ListingDetails;
};

export const RetailSaleBuyerStep = ({ listing }: RetailSaleBuyerStepProps) => {
  const { register, setValue, watch, getValues } =
    useFormContext<RetailSaleFormValues>();
  const { getUiCurrency } = useLocalizationContext();

  const optionalMsg = useContent(ContentId.Optional);
  const detailsMsg = useContent(ContentId.Details);
  const externalSaleIdMsg = useContent(ContentId.ExternalSaleId);
  const addNewBuyerMsg = useContent(ContentId.AddNewBuyer);

  const currencyCode = watch('currencyCode');
  const totalNetProceeds = watch('totalNetProceeds');

  const [selectedBuyer, setSelectedBuyer] = useState<string>(
    getValues('buyerEmail') ?? ''
  );

  const uiCurrency = getUiCurrency(currencyCode);

  const handleBuyerChange = useCallback(
    (newSelection: string) => {
      setSelectedBuyer(newSelection);
      if (newSelection === 'AddNewBuyer') {
        setValue('buyerEmail', '');
        setValue('buyerName', '');
        setValue('buyerPhone', '');
        setValue('buyerCompany', '');
      } else if (newSelection) {
        setValue('buyerEmail', newSelection);
      }
    },
    [setValue]
  );

  return (
    <Stack direction="column" gap="l">
      {/* RETAIL CUSTOMER section */}
      <DetailSection name={<Content id={ContentId.RetailCustomer} />}>
        <SectionContent numOfColumns={1}>
          <PosFormField label={<Content id={ContentId.ChooseBuyer} />}>
            <RetailBuyerSelector
              value={selectedBuyer}
              onChange={handleBuyerChange}
              className={styles.fullWidth}
              additionalOptions={{ AddNewBuyer: addNewBuyerMsg }}
              additionalOptionsPosition="top"
            />
          </PosFormField>
        </SectionContent>
        {selectedBuyer === 'AddNewBuyer' && <AddNewBuyerForm />}
      </DetailSection>

      {/* DETAIL section */}
      <DetailSection name={detailsMsg}>
        <SectionContent numOfColumns={2}>
          <PosFormField label={externalSaleIdMsg}>
            <PosTextField
              placeholder={optionalMsg}
              {...register('externalSaleId')}
            />
          </PosFormField>
          <div></div>
          <PosFormField label={<Content id={ContentId.SaleDate} />}>
            <DatePickerInput
              date={
                getValues('saleDate')
                  ? new Date(getValues('saleDate'))
                  : undefined
              }
              onDateChange={(date: Date) => {
                if (date) {
                  setValue('saleDate', date.toISOString());
                }
              }}
              locale={getLocaleFromLanguageOrCurrent()}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.InHandDate} />}>
            <DatePickerInput
              date={
                getValues('inHandDate')
                  ? new Date(getValues('inHandDate')!)
                  : undefined
              }
              onDateChange={(date: Date) => {
                if (date) {
                  setValue('inHandDate', date.toISOString());
                }
              }}
              locale={getLocaleFromLanguageOrCurrent()}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.CurrencyCode} />}>
            <CurrencyFilterSelector
              value={uiCurrency.code}
              className={styles.fullWidth}
              defaultValue={currencyCode}
              onChange={(newCurrencyCode) => {
                if (newCurrencyCode) {
                  setValue('currencyCode', newCurrencyCode);
                }
              }}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.TotalNetProceeds} />}>
            <PosCurrencyField
              uiCurrency={uiCurrency}
              value={totalNetProceeds ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const v = parseFloat(e.target.value) || 0;
                if (v >= 0 && v <= Number.MAX_VALUE) {
                  setValue('totalNetProceeds', v);
                }
              }}
            />
          </PosFormField>
          <PosFormField label={<Content id={ContentId.PrivateNote} />}>
            <PosTextArea
              placeholder={optionalMsg}
              {...register('privateNotes')}
            />
          </PosFormField>
        </SectionContent>
      </DetailSection>

      {/* ALLOCATED LISTING section */}
      <TicketSection
        maxQuantity={listing?.availQty}
        deliveryType={listing?.delivType}
        tickets={listing?.tickets ?? []}
        listing={listing}
      />

      {/* TAGS section */}
      <DetailSection name={<Content id={ContentId.Tags} />}>
        <SaleTags />
      </DetailSection>
    </Stack>
  );
};
